import * as THREE from "three";
import { of } from "rxjs";
import { switchMap, map, filter, distinctUntilChanged } from "rxjs/operators";
import { mapTypes } from "../../utils/material-utils";
import DomeMaterial from "../materials/DomeMaterial";

const CUSTOM_MATERIALS = {
  DomeMaterial,
};

class MaterialManager {
  constructor(world) {
    this.world = world;

    this.materialDefinitions = {};
    this.materialDefinitions$ = {};

    this.materials = {};
    this.materials$ = {};
  }

  getMaterialDefinition$(id) {
    return this.world.data.getMaterial$(id);
  }

  completeMaterialDefinition$(id) {
    if (!this.materialDefinitions$[id]) {
      this.materialDefinitions$[id].complete();
      delete this.materialDefinitions$[id];
    }
  }

  getMaterial(id) {
    return this.materials[id];
  }

  setMaterial(id, material) {
    this.materials[id] = material;
    const material$ = this.getMaterial$(id);
    if (material) {
      material$.next(material);
    } else {
      delete this.materialDefinitions[id];
      this.completeMaterialDefinition$(id);
    }
  }

  definitionToMaterialParameters(definition) {
    const parameters = {};

    for (const key in definition) {
      if (![...mapTypes, "id", "type", "parent"].includes(key)) {
        let value = definition[key];

        // Converts this color/emissive from sRGB to linear space.
        if (key === "color" || key === "emissive") {
          const color = new THREE.Color(value);
          color.convertSRGBToLinear();
          value = color;
        }

        // Array to vector conversions
        if (Array.isArray(value)) {
          if (value.length === 2) {
            value = new THREE.Vector2(value[0], value[1]);
          }
          if (value.length === 3) {
            value = new THREE.Vector3(value[0], value[1], value[2]);
          }
        }

        parameters[key] = value;
      }
    }

    return parameters;
  }

  createMaterial(id, definition) {
    if (!id) {
      return console.log("MaterialManager.createMaterial");
    }

    const Material =
      CUSTOM_MATERIALS[definition.type] ||
      THREE[definition.type || "MeshStandardMaterial"];

    const material = new Material(
      this.definitionToMaterialParameters(definition)
    );
    this.materials[id] = material;
    return material;
  }

  updateMaterial(id, definition) {
    const material = this.getMaterial(id);
    const parameters = this.definitionToMaterialParameters(definition);

    for (const key in parameters) {
      if (material.hasOwnProperty(key)) {
        material[key] = parameters[key];
      }
    }

    return material;
  }

  filterMapDefinitions(definition) {
    const definitionWithoutMaps = { ...definition };
    mapTypes.forEach((name) => {
      delete definitionWithoutMaps[name];
    });
    return definitionWithoutMaps;
  }

  createTextureObservable(type = "map") {
    return switchMap((result) => {
      if (result.definition && result.definition[type]) {
        return this.world.textureManager
          .getTexture$(result.definition[type])
          .pipe(
            filter((map) => map),
            map((map) => ({
              ...result,
              textures: { ...result.textures, [type]: map },
            }))
          );
      } else {
        return of(result);
      }
    });
  }

  getMaterial$(id) {
    if (!this.materials$[id]) {
      this.materials$[id] = this.getMaterialDefinition$(id).pipe(

        filter((definition) => definition),
        distinctUntilChanged(),

        switchMap((definition) => {
          if (definition && definition.parent) {
            const { parent, ...rest } = definition;
            return this.getMaterialDefinition$(parent).pipe(
              map((parentDefinition) => ({ ...parentDefinition, ...rest }))
            );
          } else {
            return of(definition);
          }
        }),

        // create / update material
        switchMap((definition) => {
          const oldMaterial = this.getMaterial(id);
          const material = oldMaterial
            ? this.updateMaterial(id, this.filterMapDefinitions(definition))
            : this.createMaterial(id, this.filterMapDefinitions(definition));
          return of({ definition, material });
        }),

        // create texture observables
        ...mapTypes.map((type) => this.createTextureObservable(type)),

        // update textures
        map(({ material, textures = {} }) => {
          let needsUpdate = false;
          mapTypes.forEach((name) => {
            if (
              material.hasOwnProperty(name) &&
              material[name] !== (textures[name] || null)
            ) {
              material[name] = textures[name] || null;
              needsUpdate = true;
            }
          });
          material.needsUpdate = needsUpdate;
          return material;
        })
      );

      // BUG: getMaterial$ -streams are not subscribed so 
      // updates don't affect
      // this is just quickfix ...
      this.materials$[id].subscribe(e => {
        this.world.change$.next();
      })
    }

    return this.materials$[id];
  }

  completeMaterial$(id) {
    if (!this.materials$[id]) {
      this.materials$[id].complete();
      delete this.materials$[id];
    }
  }
}

export default MaterialManager;
